import { useEffect, useState, useCallback } from "react";
import { CreditCard, TrendingUp, Receipt, RefreshCw, Wallet, ExternalLink, MailCheck, Search, LineChart as LineChartIcon, Plus, Banknote } from "lucide-react";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StripeConnectPanel from "@/components/StripeConnectPanel";
import api from "@/lib/api";

/**
 * Payments tab — workspace revenue command center.
 *
 * Stack:
 *   1. StripeConnectPanel (Connect / Disconnect / Dashboard)
 *   2. Revenue dashboard KPI cards (paid / awaiting / failed / refunded)
 *   3. Revenue-over-time chart (with date-range filter)
 *   4. Transaction history table (with status filter + resend invoice)
 *
 * Backend endpoints:
 *   GET /booking/payments/transactions     — list + rollups
 *   GET /booking/payments/revenue-series   — time-bucketed series
 *   POST /booking/payments/transactions/{id}/resend-invoice
 */
export default function PaymentsPanel({ workspace }) {
  const workspaceId = workspace?.workspace_id;
  const [status, setStatus] = useState(null);
  const [data, setData] = useState({ transactions: [], total: 0, totals_by_status: {}, currency: "usd" });
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [resendingId, setResendingId] = useState(null);

  const loadStatus = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const r = await api.get(`/integrations/stripe/status`, { params: { workspace_id: workspaceId } });
      setStatus(r.data);
    } catch {
      setStatus(null);
    }
  }, [workspaceId]);

  const loadTx = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const r = await api.get(`/booking/payments/transactions`, {
        params: { workspace_id: workspaceId, status: filter, limit: 100 },
      });
      setData(r.data || { transactions: [], total: 0, totals_by_status: {}, currency: "usd" });
    } catch {
      setData({ transactions: [], total: 0, totals_by_status: {}, currency: "usd" });
    } finally {
      setLoading(false);
    }
  }, [workspaceId, filter]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { loadTx(); }, [loadTx]);

  const resend = async (booking_id) => {
    setResendingId(booking_id);
    try {
      await api.post(`/booking/payments/transactions/${booking_id}/resend-invoice`);
      toast.success("Stripe invoice resent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resend invoice");
    } finally {
      setResendingId(null);
    }
  };

  const isConnected = !!(status && status.connected && status.charges_enabled);
  const t = data.totals_by_status || {};
  const cur = (data.currency || "usd").toUpperCase();
  const [manualOpen, setManualOpen] = useState(false);
  const [pendingOffline, setPendingOffline] = useState([]);

  // Pre-populate the "Record payment" picker with awaiting-offline
  // bookings — host picks one (or "free-standing") in the dialog.
  useEffect(() => {
    setPendingOffline(
      (data.transactions || []).filter(
        (tx) => tx.payment_status === "awaiting_payment" && tx.payment_method === "offline"
      )
    );
  }, [data.transactions]);

  const visibleTx = (data.transactions || []).filter((tx) => {
    if (!search.trim()) return true;
    const needle = search.toLowerCase();
    return (
      (tx.invitee_name || "").toLowerCase().includes(needle) ||
      (tx.invitee_email || "").toLowerCase().includes(needle) ||
      (tx.appointment_type_name || "").toLowerCase().includes(needle)
    );
  });

  return (
    <div className="space-y-5" data-testid="payments-panel">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#005e7a] text-white flex items-center justify-center flex-shrink-0">
            <Wallet className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h2 className="bloq-headline text-2xl">Payments</h2>
            <p className="text-sm text-[#4a4a52]">
              Connect your Stripe account to take deposits, full payments, and auto-invoice balances. Funds settle directly into your bank.
            </p>
          </div>
        </div>
        <button
          onClick={() => setManualOpen(true)}
          className="bloq-btn-secondary inline-flex items-center gap-2 text-sm"
          data-testid="record-manual-button"
        >
          <Plus className="w-4 h-4" /> Record manual payment
        </button>
      </div>

      <ManualPaymentDialog
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        workspaceId={workspaceId}
        pendingOffline={pendingOffline}
        onSaved={() => { setManualOpen(false); loadTx(); }}
      />

      {/* ── Stripe Connect ─────────────────────────────────────────── */}
      <div className="bloq-card p-5" data-testid="payments-stripe-card">
        <StripeConnectPanel workspaceId={workspaceId} returnPath="/app/bookings?tab=payments" />
      </div>

      {/* ── KPI dashboard ──────────────────────────────────────────── */}
      <div className="bloq-card p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="bloq-headline text-lg text-[#005e7a]">Revenue dashboard</h3>
            <p className="text-xs text-[#82828c] mt-0.5">
              {isConnected
                ? "Live numbers across paid, pending, and refunded transactions."
                : "Stripe payments are coming soon — once enabled, you'll track revenue, refunds, and pending invoices here."}
            </p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="revenue-kpis">
          <KpiCard
            icon={<TrendingUp className="w-4 h-4" />}
            label="Paid revenue"
            value={fmtMoney(t.paid?.amount_minor || 0, cur)}
            sub={`${t.paid?.count || 0} transactions`}
            testid="kpi-paid"
          />
          <KpiCard
            icon={<Receipt className="w-4 h-4" />}
            label="Awaiting payment"
            value={fmtMoney(t.awaiting_payment?.amount_minor || 0, cur)}
            sub={`${t.awaiting_payment?.count || 0} bookings pending`}
            testid="kpi-awaiting"
          />
          <KpiCard
            icon={<CreditCard className="w-4 h-4" />}
            label="Failed"
            value={fmtMoney(t.failed?.amount_minor || 0, cur)}
            sub={`${t.failed?.count || 0} declined`}
            testid="kpi-failed"
          />
          <KpiCard
            icon={<RefreshCw className="w-4 h-4" />}
            label="Refunded"
            value={fmtMoney(t.refunded?.amount_minor || 0, cur)}
            sub={`${t.refunded?.count || 0} refunds issued`}
            testid="kpi-refunded"
          />
        </div>
      </div>

      {/* ── Revenue over time chart ────────────────────────────────── */}
      <RevenueOverTimeCard workspaceId={workspaceId} isConnected={isConnected} />

      {/* ── Transaction history ────────────────────────────────────── */}
      <div className="bloq-card p-5" data-testid="transactions-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="bloq-headline text-lg text-[#005e7a]">Transaction history</h3>
            <p className="text-xs text-[#82828c] mt-0.5">
              {data.total} {data.total === 1 ? "transaction" : "transactions"} on file
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#82828c]" />
              <input
                type="search"
                placeholder="Search name, email, type…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bloq-input pl-8 h-9 text-xs w-60"
                data-testid="tx-search"
              />
            </div>
            <FilterPill v={filter} setV={setFilter} k="all" label="All" testidPrefix="tx-filter" />
            <FilterPill v={filter} setV={setFilter} k="paid" label="Paid" testidPrefix="tx-filter" />
            <FilterPill v={filter} setV={setFilter} k="awaiting_payment" label="Pending" testidPrefix="tx-filter" />
            <FilterPill v={filter} setV={setFilter} k="refunded" label="Refunded" testidPrefix="tx-filter" />
            <FilterPill v={filter} setV={setFilter} k="failed" label="Failed" testidPrefix="tx-filter" />
          </div>
        </div>

        {loading ? (
          <p className="text-center text-sm text-[#82828c] py-10">Loading…</p>
        ) : visibleTx.length === 0 ? (
          <EmptyTxState isConnected={isConnected} hasFilter={filter !== "all" || !!search.trim()} />
        ) : (
          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-sm" data-testid="tx-table">
              <thead className="text-[10px] uppercase tracking-wider text-[#82828c] border-b border-[#e3dccc]">
                <tr>
                  <th className="text-left py-2 pr-3">Invitee</th>
                  <th className="text-left py-2 pr-3">Appointment</th>
                  <th className="text-left py-2 pr-3">Status</th>
                  <th className="text-left py-2 pr-3">Method</th>
                  <th className="text-right py-2 pr-3">Amount</th>
                  <th className="text-right py-2 pr-3">Balance invoice</th>
                  <th className="text-right py-2">Created</th>
                </tr>
              </thead>
              <tbody>
                {visibleTx.map((tx) => (
                  <TxRow key={tx.booking_id} tx={tx} cur={cur} onResend={resend} resending={resendingId === tx.booking_id} onRecordPayment={() => { setManualOpen(true); }} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function FilterPill({ v, setV, k, label, testidPrefix = "filter" }) {
  const active = v === k;
  return (
    <button
      type="button"
      onClick={() => setV(k)}
      className={`text-xs px-3 py-1.5 transition-colors ${active ? "bg-[#005e7a] text-white" : "bg-[#f5efe6] text-[#4a4a52] hover:bg-[#ebe3d1]"}`}
      style={{ borderRadius: 9999 }}
      data-testid={`${testidPrefix}-${k}`}
    >
      {label}
    </button>
  );
}

function RevenueOverTimeCard({ workspaceId, isConnected }) {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState({ buckets: [], total_revenue_minor: 0, total_transactions: 0, currency: "usd", granularity: "day" });
  const [loading, setLoading] = useState(true);
  const [customOpen, setCustomOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const load = useCallback(async (rangeKey, from, to) => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const params = { workspace_id: workspaceId, range: rangeKey };
      if (rangeKey === "custom" && from && to) {
        params.from = from;
        params.to = to;
      }
      const r = await api.get(`/booking/payments/revenue-series`, { params });
      setData(r.data || { buckets: [], total_revenue_minor: 0, total_transactions: 0, currency: "usd", granularity: "day" });
    } catch {
      setData({ buckets: [], total_revenue_minor: 0, total_transactions: 0, currency: "usd", granularity: "day" });
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (range !== "custom") load(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, workspaceId]);

  const applyCustom = () => {
    if (!customFrom || !customTo) {
      toast.error("Pick both start and end dates");
      return;
    }
    setRange("custom");
    const fromIso = new Date(`${customFrom}T00:00:00Z`).toISOString();
    const toIso = new Date(`${customTo}T23:59:59Z`).toISOString();
    load("custom", fromIso, toIso);
    setCustomOpen(false);
  };

  const cur = (data.currency || "usd").toUpperCase();
  const chartData = (data.buckets || []).map((b) => ({
    label: b.label,
    revenue: (b.revenue_minor || 0) / 100,
    transactions: b.transactions || 0,
    iso: b.iso,
  }));
  const peak = chartData.reduce((acc, p) => Math.max(acc, p.revenue), 0);

  return (
    <div className="bloq-card p-5" data-testid="revenue-over-time">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-[#005e7a]/10 text-[#005e7a] flex items-center justify-center flex-shrink-0">
            <LineChartIcon className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <h3 className="bloq-headline text-lg text-[#005e7a]">Revenue over time</h3>
            <p className="text-xs text-[#82828c] mt-0.5">
              {loading ? "Loading…" : (
                <>
                  <strong className="text-[#1a1a1f]">{fmtMoney(data.total_revenue_minor, cur)}</strong>
                  {" "}across {data.total_transactions} {data.total_transactions === 1 ? "transaction" : "transactions"} · grouped by {data.granularity}
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="revenue-range-filter">
          <FilterPill v={range} setV={setRange} k="7d" label="7d" testidPrefix="revenue-filter" />
          <FilterPill v={range} setV={setRange} k="30d" label="30d" testidPrefix="revenue-filter" />
          <FilterPill v={range} setV={setRange} k="90d" label="90d" testidPrefix="revenue-filter" />
          <FilterPill v={range} setV={setRange} k="ytd" label="YTD" testidPrefix="revenue-filter" />
          <FilterPill v={range} setV={setRange} k="12m" label="12m" testidPrefix="revenue-filter" />
          <button
            type="button"
            onClick={() => setCustomOpen((v) => !v)}
            className={`text-xs px-3 py-1.5 transition-colors ${range === "custom" ? "bg-[#005e7a] text-white" : "bg-[#f5efe6] text-[#4a4a52] hover:bg-[#ebe3d1]"}`}
            style={{ borderRadius: 9999 }}
            data-testid="revenue-filter-custom"
          >
            Custom
          </button>
        </div>
      </div>

      {customOpen && (
        <div className="flex items-end gap-2 mb-4 bg-[#f5efe6] p-3 rounded-lg" data-testid="revenue-custom-range">
          <label className="text-xs text-[#4a4a52] flex flex-col gap-1">
            From
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="bloq-input h-9 text-xs" data-testid="revenue-from-date" />
          </label>
          <label className="text-xs text-[#4a4a52] flex flex-col gap-1">
            To
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="bloq-input h-9 text-xs" data-testid="revenue-to-date" />
          </label>
          <button onClick={applyCustom} className="bloq-btn-primary text-xs h-9" data-testid="revenue-apply-custom">Apply</button>
        </div>
      )}

      {loading ? (
        <div className="h-64 flex items-center justify-center text-sm text-[#82828c]">Loading chart…</div>
      ) : peak === 0 ? (
        <div className="h-64 flex flex-col items-center justify-center text-sm text-[#82828c] text-center px-6" data-testid="revenue-empty">
          {isConnected ? (
            <>
              <p className="font-medium text-[#4a4a52] mb-1">No paid revenue in this period</p>
              <p className="text-xs">Adjust the range above, or wait for your next paid booking to land.</p>
            </>
          ) : (
            <>
              <p className="font-medium text-[#4a4a52] mb-1">Stripe payments — coming soon</p>
              <p className="text-xs">Once Stripe is enabled, your paid bookings will populate this chart automatically.</p>
            </>
          )}
        </div>
      ) : (
        <div className="h-64 w-full" data-testid="revenue-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="revArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#005e7a" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#005e7a" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0eadc" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#82828c" }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
                minTickGap={20}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#82828c" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k` : `$${v.toFixed(0)}`)}
                width={48}
              />
              <Tooltip
                cursor={{ stroke: "#005e7a", strokeWidth: 1, strokeOpacity: 0.3 }}
                contentStyle={{
                  background: "#ffffff",
                  border: "1px solid #e3dccc",
                  borderRadius: 8,
                  fontSize: 12,
                  padding: "8px 12px",
                }}
                formatter={(value, name) => {
                  if (name === "revenue") return [fmtMoney(Math.round(value * 100), cur), "Revenue"];
                  return [value, name];
                }}
                labelFormatter={(l) => l}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#005e7a"
                strokeWidth={2}
                fill="url(#revArea)"
                activeDot={{ r: 4, fill: "#005e7a" }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function KpiCard({ icon, label, value, sub, testid }) {
  return (
    <div className="bg-[#f5efe6] p-4 rounded-lg" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-[#82828c] mb-2">
        <span className="text-[#005e7a]">{icon}</span>
        <span>{label}</span>
      </div>
      <p className="text-2xl font-bold text-[#1a1a1f]">{value}</p>
      <p className="text-[11px] text-[#82828c] mt-1">{sub}</p>
    </div>
  );
}

function TxRow({ tx, cur, onResend, resending, onRecordPayment }) {
  const dt = tx.created_at ? new Date(tx.created_at) : null;
  const statusColor = STATUS_STYLE[tx.payment_status] || { bg: "rgba(130,130,140,0.15)", color: "#4a4a52", label: tx.payment_status };
  const hasInvoice = !!tx.stripe_balance_invoice_url;
  const method = tx.payment_method || (tx.manual_payment_method ? "offline" : "stripe");
  const methodLabel = method === "offline"
    ? METHOD_LABELS[tx.manual_payment_method] || "Offline"
    : "Stripe";
  const methodIcon = method === "offline"
    ? <Banknote className="w-3.5 h-3.5" />
    : <CreditCard className="w-3.5 h-3.5" />;
  const canRecordPayment = tx.payment_status === "awaiting_payment" && method === "offline";
  return (
    <tr className="border-b border-[#f0eadc] hover:bg-[#fafaf3]" data-testid={`tx-row-${tx.booking_id}`}>
      <td className="py-3 pr-3 align-top">
        <p className="font-medium text-[#1a1a1f]">{tx.invitee_name || "—"}</p>
        <p className="text-xs text-[#82828c]">{tx.invitee_email || ""}</p>
      </td>
      <td className="py-3 pr-3 align-top text-[#4a4a52]">
        <p>{tx.appointment_type_name || "—"}</p>
        {tx.payment_mode && (
          <p className="text-[10px] uppercase tracking-wider text-[#82828c] mt-0.5">{tx.payment_mode}</p>
        )}
      </td>
      <td className="py-3 pr-3 align-top">
        <span
          className="bloq-pill text-[10px] uppercase tracking-wider font-semibold"
          style={{ background: statusColor.bg, color: statusColor.color }}
        >
          {statusColor.label}
        </span>
        {canRecordPayment && (
          <button
            onClick={onRecordPayment}
            className="block text-[10px] text-[#005e7a] hover:underline mt-1.5"
            data-testid={`tx-record-payment-${tx.booking_id}`}
          >
            Record payment →
          </button>
        )}
      </td>
      <td className="py-3 pr-3 align-top">
        <div className="inline-flex items-center gap-1.5 text-xs text-[#4a4a52]">
          <span className="text-[#005e7a]">{methodIcon}</span>
          <span>{methodLabel}</span>
        </div>
        {tx.manual_payment_reference && (
          <p className="text-[10px] text-[#82828c] mt-0.5">{tx.manual_payment_reference}</p>
        )}
      </td>
      <td className="py-3 pr-3 align-top text-right">
        <p className="font-medium">{fmtMoney(tx.stripe_payment_amount_minor || 0, (tx.stripe_payment_currency || cur).toUpperCase())}</p>
        {tx.total_amount_minor != null && tx.total_amount_minor > (tx.stripe_payment_amount_minor || 0) && (
          <p className="text-[10px] text-[#82828c]">
            of {fmtMoney(tx.total_amount_minor, (tx.stripe_payment_currency || cur).toUpperCase())} total
          </p>
        )}
      </td>
      <td className="py-3 pr-3 align-top text-right text-xs">
        {hasInvoice ? (
          <div className="inline-flex items-center gap-2">
            <a
              href={tx.stripe_balance_invoice_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#005e7a] underline inline-flex items-center gap-1"
              data-testid={`tx-invoice-link-${tx.booking_id}`}
            >
              View <ExternalLink className="w-3 h-3" />
            </a>
            <button
              onClick={() => onResend(tx.booking_id)}
              disabled={resending}
              className="text-[#005e7a] hover:underline inline-flex items-center gap-1 disabled:opacity-50"
              title="Resend invoice email"
              data-testid={`tx-invoice-resend-${tx.booking_id}`}
            >
              <MailCheck className="w-3 h-3" /> {resending ? "Sending…" : "Resend"}
            </button>
          </div>
        ) : (
          <span className="text-[#82828c]">—</span>
        )}
      </td>
      <td className="py-3 align-top text-right text-xs text-[#82828c]">
        {dt ? dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"}
      </td>
    </tr>
  );
}

const METHOD_LABELS = {
  cash:          "Cash",
  check:         "Check",
  bank_transfer: "Bank transfer",
  venmo:         "Venmo",
  cashapp:       "Cash App",
  paypal:        "PayPal",
  zelle:         "Zelle",
  other:         "Other",
};

function ManualPaymentDialog({ open, onClose, workspaceId, pendingOffline, onSaved }) {
  // linkMode: how the payment is attached.
  //   booking → pick a pending offline booking
  //   apt     → link to an appointment type (creates a synthetic booking against that type)
  //   free    → completely standalone (custom name + label)
  const [linkMode, setLinkMode] = useState("booking");
  const [bookingId, setBookingId] = useState("");
  const [aptTypeId, setAptTypeId] = useState("");
  const [aptTypes, setAptTypes] = useState([]);
  // Contact fields — mirror the booking-form schema so manual
  // payments populate the same /contacts CRM record.
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [inviteeEmail, setInviteeEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [appointmentLabel, setAppointmentLabel] = useState("");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);

  // Default to whichever mode has data. If no pending offline bookings,
  // start on "apt" since that's the next-most-useful path.
  useEffect(() => {
    if (open) {
      setLinkMode(pendingOffline && pendingOffline.length > 0 ? "booking" : "apt");
      setBookingId("");
      setAptTypeId("");
      setFirstName("");
      setLastName("");
      setInviteeEmail("");
      setPhone("");
      setCompany("");
      setAppointmentLabel("");
      setAmount("");
      setMethod("cash");
      setReference("");
      setNotes("");
      setPaidAt(new Date().toISOString().slice(0, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Lazy-load appointment types when the dialog opens — we need them
  // for the "Link to appointment type" mode.
  useEffect(() => {
    if (!open || !workspaceId) return;
    let alive = true;
    api.get(`/booking/appointment-types`, { params: { workspace_id: workspaceId } })
      .then((r) => { if (alive) setAptTypes((r.data || []).filter((a) => a.active !== false)); })
      .catch(() => { if (alive) setAptTypes([]); });
    return () => { alive = false; };
  }, [open, workspaceId]);

  // Auto-populate amount + invitee when host picks a pending booking.
  useEffect(() => {
    if (linkMode !== "booking" || !bookingId) return;
    const picked = (pendingOffline || []).find((b) => b.booking_id === bookingId);
    if (picked) {
      setAmount(((picked.stripe_payment_amount_minor || picked.total_amount_minor || 0) / 100).toFixed(2));
      // Split a single-line name into first/last for back-compat
      // (old bookings only store invitee_name).
      const first = picked.invitee_first_name || (picked.invitee_name || "").split(" ")[0] || "";
      const last = picked.invitee_last_name || (picked.invitee_name || "").split(" ").slice(1).join(" ") || "";
      setFirstName(first);
      setLastName(last);
      setInviteeEmail(picked.invitee_email || "");
      setPhone(picked.invitee_phone || "");
      setCompany(picked.invitee_company || "");
      setAppointmentLabel(picked.appointment_type_name || "");
    }
  }, [linkMode, bookingId, pendingOffline]);

  // Auto-populate amount + label when host picks an appointment type.
  useEffect(() => {
    if (linkMode !== "apt" || !aptTypeId) return;
    const picked = aptTypes.find((a) => a.appointment_type_id === aptTypeId);
    if (picked) {
      setAmount(picked.price_amount ? Number(picked.price_amount).toFixed(2) : "");
      setAppointmentLabel(picked.name || "");
    }
  }, [linkMode, aptTypeId, aptTypes]);

  const submit = async () => {
    if (!amount || Number(amount) <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }
    if (linkMode === "booking" && !bookingId) {
      toast.error("Pick a pending booking, or switch to another mode");
      return;
    }
    if (linkMode === "apt" && !aptTypeId) {
      toast.error("Pick an appointment type, or switch to another mode");
      return;
    }
    if (linkMode !== "booking" && !firstName.trim()) {
      toast.error("First name is required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        workspace_id: workspaceId,
        amount: Number(amount),
        currency: "usd",
        method,
        reference: reference.trim() || undefined,
        notes: notes.trim() || undefined,
        paid_at: paidAt ? new Date(`${paidAt}T12:00:00Z`).toISOString() : undefined,
      };
      if (linkMode === "booking") {
        payload.booking_id = bookingId;
        // Send contact fields too so we can update the CRM record at
        // payment time (e.g., adding a phone the visitor didn't share).
        if (firstName.trim()) payload.invitee_first_name = firstName.trim();
        if (lastName.trim()) payload.invitee_last_name = lastName.trim();
        if (phone.trim()) payload.invitee_phone = phone.trim();
        if (company.trim()) payload.invitee_company = company.trim();
      } else {
        payload.invitee_first_name = firstName.trim();
        payload.invitee_last_name = lastName.trim() || undefined;
        payload.invitee_email = inviteeEmail.trim() || undefined;
        payload.invitee_phone = phone.trim() || undefined;
        payload.invitee_company = company.trim() || undefined;
        payload.appointment_type_name = appointmentLabel.trim() || undefined;
        if (linkMode === "apt") payload.appointment_type_id = aptTypeId;
      }
      await api.post(`/booking/payments/manual`, payload);
      toast.success("Payment recorded");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record payment");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="manual-payment-dialog">
        <DialogHeader>
          <DialogTitle>Record manual payment</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {/* ── Link mode radio toggle ──────────────────────────── */}
          <div>
            <label className="text-xs uppercase tracking-wider text-[#82828c] block mb-1.5">Link this payment to</label>
            <div className="grid grid-cols-3 gap-1.5 p-1 bg-[#f5efe6]" style={{ borderRadius: 9999 }} data-testid="manual-link-mode">
              <LinkModeOption v={linkMode} setV={setLinkMode} k="booking" label="Existing booking" disabled={!pendingOffline || pendingOffline.length === 0} />
              <LinkModeOption v={linkMode} setV={setLinkMode} k="apt" label="Appointment type" />
              <LinkModeOption v={linkMode} setV={setLinkMode} k="free" label="Free-standing" />
            </div>
            <p className="text-[11px] text-[#82828c] mt-1.5">
              {linkMode === "booking" && (pendingOffline?.length ? "Mark a pending offline booking as paid." : "No offline bookings are awaiting payment.")}
              {linkMode === "apt" && "Creates a completed booking against the selected appointment type so it shows up in bookings + revenue."}
              {linkMode === "free" && "Standalone payment — useful for back-filling old appointments or custom one-offs."}
            </p>
          </div>

          {/* ── Mode-specific picker ─────────────────────────────── */}
          {linkMode === "booking" && (
            <div>
              <label className="text-xs uppercase tracking-wider text-[#82828c] block mb-1.5">Pending booking *</label>
              <Select value={bookingId} onValueChange={setBookingId}>
                <SelectTrigger data-testid="manual-booking-select"><SelectValue placeholder="Pick a booking…" /></SelectTrigger>
                <SelectContent>
                  {(pendingOffline || []).map((b) => (
                    <SelectItem key={b.booking_id} value={b.booking_id}>
                      {b.invitee_name || "(unnamed)"} · {b.appointment_type_name || "appointment"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {linkMode === "apt" && (
            <div>
              <label className="text-xs uppercase tracking-wider text-[#82828c] block mb-1.5">Appointment type *</label>
              <Select value={aptTypeId} onValueChange={setAptTypeId}>
                <SelectTrigger data-testid="manual-apt-select"><SelectValue placeholder="Pick an appointment type…" /></SelectTrigger>
                <SelectContent>
                  {aptTypes.length === 0 ? (
                    <SelectItem value="_none" disabled>No active appointment types</SelectItem>
                  ) : aptTypes.map((a) => (
                    <SelectItem key={a.appointment_type_id} value={a.appointment_type_id}>
                      {a.name}{a.price_amount ? ` · $${a.price_amount}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* ── Contact fields ──────────────────────────────────── */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#82828c] block mb-1.5">First name *</label>
              <input className="bloq-input" value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="manual-first-name" placeholder="John" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#82828c] block mb-1.5">Last name</label>
              <input className="bloq-input" value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="manual-last-name" placeholder="Doe" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#82828c] block mb-1.5">Email{linkMode === "booking" ? " (read-only)" : ""}</label>
              <input className="bloq-input" type="email" value={inviteeEmail} onChange={(e) => setInviteeEmail(e.target.value)} disabled={linkMode === "booking"} data-testid="manual-email" placeholder="john@example.com" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#82828c] block mb-1.5">Phone (optional)</label>
              <input className="bloq-input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="manual-phone" placeholder="555-1234" />
            </div>
            <div className="col-span-2">
              <label className="text-xs uppercase tracking-wider text-[#82828c] block mb-1.5">Company (optional)</label>
              <input className="bloq-input" value={company} onChange={(e) => setCompany(e.target.value)} data-testid="manual-company" placeholder="Acme Inc." />
            </div>
            {linkMode === "free" && (
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-wider text-[#82828c] block mb-1.5">Appointment / description</label>
                <input className="bloq-input" value={appointmentLabel} onChange={(e) => setAppointmentLabel(e.target.value)} data-testid="manual-label" placeholder="60-min consultation" />
              </div>
            )}
          </div>

          {/* ── Payment details ─────────────────────────────────── */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#82828c] block mb-1.5">Amount (USD) *</label>
              <input className="bloq-input" type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="manual-amount" placeholder="0.00" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#82828c] block mb-1.5">Method *</label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="manual-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                  <SelectItem value="venmo">Venmo</SelectItem>
                  <SelectItem value="cashapp">Cash App</SelectItem>
                  <SelectItem value="paypal">PayPal</SelectItem>
                  <SelectItem value="zelle">Zelle</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#82828c] block mb-1.5">Reference</label>
              <input className="bloq-input" value={reference} onChange={(e) => setReference(e.target.value)} data-testid="manual-reference" placeholder="Check #1234, Venmo ID, etc." />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#82828c] block mb-1.5">Paid on</label>
              <input className="bloq-input" type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} data-testid="manual-paid-at" />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-[#82828c] block mb-1.5">Notes</label>
            <textarea className="bloq-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="manual-notes" placeholder="Anything you want to remember about this payment" />
          </div>
        </div>

        <DialogFooter className="mt-4">
          <button onClick={onClose} className="bloq-btn-secondary" data-testid="manual-cancel">Cancel</button>
          <button onClick={submit} disabled={submitting} className="bloq-btn-primary disabled:opacity-60" data-testid="manual-save">
            {submitting ? "Saving…" : "Record payment"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LinkModeOption({ v, setV, k, label, disabled }) {
  const active = v === k;
  return (
    <button
      type="button"
      onClick={() => !disabled && setV(k)}
      disabled={disabled}
      className={`text-xs py-1.5 px-2 transition-colors ${active ? "bg-[#005e7a] text-white shadow-sm" : "text-[#4a4a52] hover:bg-[#ebe3d1]"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
      style={{ borderRadius: 9999 }}
      data-testid={`manual-link-${k}`}
    >
      {label}
    </button>
  );
}

function EmptyTxState({ isConnected, hasFilter }) {
  return (
    <div className="text-center py-10 text-[#82828c] text-sm" data-testid="tx-empty">
      {hasFilter ? (
        <p>No transactions match the current filter.</p>
      ) : isConnected ? (
        <>
          <p className="font-medium text-[#4a4a52] mb-1">No transactions yet</p>
          <p className="text-xs">Set an appointment type to <strong>Take a deposit</strong> or <strong>Charge full price</strong> and your first paid booking will land here.</p>
        </>
      ) : (
        <>
          <p className="font-medium text-[#4a4a52] mb-1">Stripe payments — coming soon</p>
          <p className="text-xs">Once Stripe is enabled, all paid bookings will appear in this table automatically.</p>
        </>
      )}
    </div>
  );
}

const STATUS_STYLE = {
  paid:             { bg: "rgba(34,197,94,0.15)",  color: "#15803d", label: "Paid" },
  awaiting_payment: { bg: "rgba(180,83,9,0.15)",   color: "#b45309", label: "Pending" },
  failed:           { bg: "rgba(220,38,38,0.15)",  color: "#b91c1c", label: "Failed" },
  refunded:         { bg: "rgba(130,130,140,0.18)",color: "#4a4a52", label: "Refunded" },
};

function fmtMoney(minor, currency = "USD") {
  const n = (minor || 0) / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: n === Math.floor(n) ? 0 : 2,
    }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}
